import { Marketplace } from 'APIs/marketplaces/marketplaces.types';
import Spinner from 'Components/Spinner/Spinner';
import { FunctionComponent, useMemo } from 'react';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { MdOutlineSettings } from 'react-icons/md';
import { NavLink } from 'react-router-dom';

import EmptyComponent from './EmptyComponent';
import ErrorComponent from './ErrorComponent';
import { TableStyleSettings, TableThemeSettings } from './constants';
import { MarketplaceDataTableContainer, MessageWrapper } from './styles';

// createTheme creates a new theme named solarized that overrides the build in dark theme
createTheme('polarized', TableThemeSettings);

interface Props {
  marketplaceList?: Array<Marketplace>;
  fetchListLoader: boolean;
  totalRows: number;
  handlePerRowsChange: (newPerPage: number, page: number) => void;
  handlePageChange: (page: number) => void;
  limit: number;
  activePage: number;
  isErrorRequest?: boolean;
}

function MarketplaceDataTable({
  marketplaceList,
  totalRows,
  limit,
  fetchListLoader,
  handlePageChange,
  handlePerRowsChange,
  activePage,
  isErrorRequest,
}: Props) {
  const ManageIcon = MdOutlineSettings as FunctionComponent;

  const columns: TableColumn<Marketplace>[] = useMemo(() => {
    return [
      {
        name: 'Marketplace ID',
        selector: (row: Marketplace) => row.marketplaceId,
        grow: 4,
      },
      {
        name: 'Slug',
        selector: (row: Marketplace) => row.slug,
        grow: 6,
      },
      {
        name: 'Business name',
        selector: (row: Marketplace) => row.businessName,
        grow: 4,
      },
      {
        name: 'Website',
        selector: (row: Marketplace) => row.website,
        grow: 4,
        left: true,
      },
      {
        name: 'Enable customer users',
        selector: (row: Marketplace) => (row.enableMarketplaceCustomerUsers ? 'Yes' : 'No'),
        grow: 1,
        center: true,
      },
      {
        name: '',
        cell: (row: Marketplace) => (
          <div className="hover-button-wrapper">
            <NavLink className="link-btn nav-link" to={`/admin-dashboard/marketplaces/${row.marketplaceId}/merchants`}>
              <ManageIcon />
            </NavLink>
          </div>
        ),
        grow: 2,
        center: true,
      },
    ];
  }, [ManageIcon]);

  return (
    <MarketplaceDataTableContainer>
      <DataTable
        pagination
        responsive
        columns={columns}
        data={marketplaceList ?? []}
        customStyles={TableStyleSettings}
        theme="polarized"
        className=""
        progressPending={fetchListLoader}
        progressComponent={
          <MessageWrapper>
            <Spinner color="#0016D1" height="25px" width="25px" />
          </MessageWrapper>
        }
        noDataComponent={
          isErrorRequest ? (
            <MessageWrapper>
              <ErrorComponent />
            </MessageWrapper>
          ) : (
            <MessageWrapper>
              <EmptyComponent />
            </MessageWrapper>
          )
        }
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationPerPage={limit}
        paginationDefaultPage={activePage}
        sortServer
        persistTableHead
        defaultSortFieldId={4}
        defaultSortAsc={false}
        paginationRowsPerPageOptions={[20, 40, 60, 80, 100]}
      />
    </MarketplaceDataTableContainer>
  );
}

export default MarketplaceDataTable;
